import { fabric } from 'fabric'

import { ObjectType } from "../common/constants";

// Color helpers moved to '@/utils/color' so components outside the editor can
// use them without importing this fabric-dependent module. Re-exported here so
// existing `from './utils'` / `from '@/scenes/engine/utils/utils'` imports keep working.
export { hexToRgb, hexToRgba, hexToHexa, isColorBright } from '@/utils/color'

export function normalize(
    value: number, 
    minInputValue: number, 
    maxInputValue: number, 
    minOutputValue: number, 
    maxOutputValue: number
): number {
    let normalizedBzrtValue = (value - minInputValue) / Math.abs(maxInputValue - minInputValue);
    let normalizedFilterValue = normalizedBzrtValue * (maxOutputValue - minOutputValue) + minOutputValue;
    return normalizedFilterValue;
}

// Create a stable fingerprint for a set of fabric objects using their bazaartGuid
export const createFingerprint = (objs: fabric.Object[]): string => objs.map((o: any) => o.bazaartGuid).sort().join('|');

export interface FontMetrics {
    ascent: number, 
    descent: number,
    width: number,
    leading: number,
    lineHeight: number
};
  
export  interface FabricFontMetrics extends FontMetrics {
    leading: number,
    textHeight: number,
    lineHeightRatio: number
};
  
// https://stackoverflow.com/questions/1134586/how-can-you-find-the-height-of-text-on-an-html-canvas
function getFontMetrics(text: string, font: string): FontMetrics {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = font;

    // Measure the text
    const textMetrics = context.measureText(text);
    // https://chatgpt.com/share/e/6739f859-44b0-8009-b614-31b646a04af9
    let webDPI = 96;
    let mobilePrintDpi = 72;
    let dpiRatio = mobilePrintDpi / webDPI;
    let ascent = textMetrics.fontBoundingBoxAscent * dpiRatio;
    let descent = textMetrics.fontBoundingBoxDescent * dpiRatio;
    let lineHeight = (ascent + descent) 

    return {
      ascent: ascent,
      descent: descent,
      width: textMetrics.width,
      lineHeight: lineHeight,
      leading: 0
    }
  }
// Function to get Fabric font metrics including lineHeight
export function getFabricFontMetrics(fontSize: number, fontFamily: string, text: string): FabricFontMetrics {
    const font = `${fontSize}pt ${fontFamily}`;
    const textMetrics = getFontMetrics(text, font);
    const fontMetrics = getFontMetrics('Hg', font);

    return {
        ascent: fontMetrics.ascent,
        descent: fontMetrics.descent,
        width: textMetrics.width,
        leading: textMetrics.leading,
        lineHeight: fontMetrics.lineHeight,
        lineHeightRatio: fontMetrics.lineHeight / fontSize,
        textHeight: fontMetrics.ascent + fontMetrics.descent
    };
}

interface IDefaultLayer {
    type: string;
    width: number;
    guid: string;
    assetStateId: string;
    sceneId: string;
    hasTransparency?: boolean;
    isStockImage?: boolean;
    offsetX?: number | undefined;
    offsetY?: number | undefined;
    overrides?: Partial<Record<string, any>>;
    didUserUploadPhoto?: boolean;
}
export function createDefaultLayer(defaultLayer: IDefaultLayer) {
    const object: any = {
        type: defaultLayer.type,
        centerPoint: { x: 0.5, y: 0.5 },
        sizeOnCanvas: { width: defaultLayer.width },
        transformation: { horizontalFlip: false, verticalFlip: false },
        boundingBox: { x: 0, y: 0, width: 1, height: 1 },
        absoluteRotation: 0,
        bazaartGuid: defaultLayer.guid,
        layerAssetStateId: defaultLayer.assetStateId,
        // New layers need to wait for image initialization before they're ready
        isInitializedNormalizeMask: false,
        isInitializedNormalizedImage: false,
        ...defaultLayer.overrides // inject custom props if needed
    };

    if (defaultLayer.sceneId != null) {
        object.keyFrames = {
            [defaultLayer.sceneId]: {
                layoutInfo: {
                    sizeOnCanvas: { width: defaultLayer.width },
                    opacity: 1,
                    absoluteRotation: 0,
                    center: { x: 0.5, y: 0.5 }
                }
            }
        };
    }
    
    if (defaultLayer.hasTransparency !== undefined) object.hasTransparency = defaultLayer.hasTransparency;
    if (defaultLayer.isStockImage) object.isStockImage = true;
    if (defaultLayer.offsetX !== undefined) object.offsetX = defaultLayer.offsetX;
    if (defaultLayer.offsetY !== undefined) object.offsetY = defaultLayer.offsetY;

    return object;
}

/**
 * Converts Bazaart object types to their static equivalents for export/undo operations
 * @param obj - The object to transform
 * @returns The transformed object with updated type
 */
export function replaceType(obj: any): any {
  obj.previousType = obj.type
  switch (obj.type) {
    case ObjectType.BAZAART_BG:
      obj.type = ObjectType.BACKGROUND_IMAGE
      break
    case ObjectType.BAZAART_TEXT:
      obj.type = ObjectType.OPEN_TYPE_TEXT
      break
    case ObjectType.BAZAART_STICKER:
    case ObjectType.BAZAART_IMAGE:
    case ObjectType.BAZAART_SHAP:
    case ObjectType.BAZAART_DRAWING:
    case ObjectType.BAZAART_OVERLAY:
    case ObjectType.BAZAART_IMAGE_FROM_TEXT:
      obj.type = ObjectType.STATIC_IMAGE
      break
    case ObjectType.GROUP:
    case ObjectType.BAZAART_GROUP_BREAKTEXT:
      obj.objects = obj.objects.map((groupObj: any) => {
        return replaceType(groupObj)
      })
      break
  }
  return obj
}